import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import { Logoicon1 } from "../../icons/Logoicon1";
import { Logoicon3 } from "../../icons/Logoicon3";
import { isAuthenticated, getCurrentUser } from "../../api";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { navigateToContactSection } from "../../utils/contactNavigation";
import { recordPage } from "../../analytics";

/* ── constants ───────────────────────────────────────────── */
const NAV_ITEMS = [
  { label: "Home", route: "/" },
  { label: "Services", route: "/services" },
  { label: "About", route: "/about" },
  { label: "Blog", route: "/blogs" },
] as const;

const HEADER_HEIGHT = 64; // px — single source of truth

/* ── icons ───────────────────────────────────────────────── */
const LinkedinIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);
const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);
const CloseIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.25"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6 6l12 12M18 6 6 18"
    />
  </svg>
);

/* ── animated hamburger lines ────────────────────────────── */
const HamburgerIcon = ({ open }: { open: boolean }) => (
  <div className="relative flex h-5 w-5 flex-col items-center justify-center">
    <span
      className={`absolute h-0.5 w-5 rounded-full bg-current transition-all duration-300 ease-out ${
        open ? "translate-y-0 rotate-45" : "-translate-y-[6px]"
      }`}
    />
    <span
      className={`absolute h-0.5 w-5 rounded-full bg-current transition-all duration-200 ease-out ${
        open ? "scale-x-0 opacity-0" : "scale-x-100 opacity-100"
      }`}
    />
    <span
      className={`absolute h-0.5 w-5 rounded-full bg-current transition-all duration-300 ease-out ${
        open ? "translate-y-0 -rotate-45" : "translate-y-[6px]"
      }`}
    />
  </div>
);

/* ════════════════════════════════════════════════════════════
   Header — unified component for desktop & mobile
   ════════════════════════════════════════════════════════════ */
export const Header = ({ device }: { device: string }) => {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    recordPage(location.pathname);
  }, [location.pathname]);
  const prevPathnameRef = useRef(location.pathname);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [authenticated, setAuthenticated] = useState(false);
  const isMobile = device === "mob";

  /* ── scroll-aware glass effect ─────────────────────────── */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* ── auth ───────────────────────────────────────────────── */
  useEffect(() => {
    const check = () => {
      setAuthenticated(isAuthenticated());
      setUser(getCurrentUser());
    };
    check();
    window.addEventListener("storage", check);
    return () => window.removeEventListener("storage", check);
  }, []);

  /* ── close menu on route change ────────────────────────── */
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  /* ── lock body scroll while menu is open ───────────────── */
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  /* ── reset scroll on route change (skip hash navigations) */
  useLayoutEffect(() => {
    const changed = prevPathnameRef.current !== location.pathname;
    prevPathnameRef.current = location.pathname;

    if (!changed || location.hash) return;

    const reset = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      [document.scrollingElement, document.documentElement, document.body]
        .filter((el): el is HTMLElement => el instanceof HTMLElement)
        .forEach((el) => {
          el.scrollTop = 0;
        });
    };
    reset();
    const raf = requestAnimationFrame(reset);
    const timer = setTimeout(reset, 80);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [location.pathname, location.hash]);

  /* ── handlers ──────────────────────────────────────────── */
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const toggleMenu = useCallback(() => setMenuOpen((p) => !p), []);

  const goHome = () => {
    window.scrollTo(0, 0);
    window.location.reload();
  };

  const goToRoute = (route: string) => {
    closeMenu();
    navigate(route);
  };

  const handleContactClick = () => {
    closeMenu();
    navigateToContactSection(navigate, location.pathname);
  };

  const handleLogin = () => {
    closeMenu();
    navigate("/auth");
  };

  const handleAdminAccess = () => {
    closeMenu();
    navigate("/admin/dashboard");
  };

  const isActive = (route: string) =>
    location.pathname === route ||
    (route === "/" && location.pathname === "/home");

  /* ── logo (shared between mobile/desktop) ──────────────── */
  const LogoIcon = isMobile ? Logoicon1 : Logoicon3;

  /* ════════════════════════════════════════════════════════ */
  /*  Render                                                  */
  /* ════════════════════════════════════════════════════════ */
  return (
    <>
      {/* ─── Fixed header bar ─────────────────────────────── */}
      <header
        className={`fixed inset-x-0 top-0 z-50 ${
          menuOpen
            ? "bg-transparent transition-all duration-100"
            : `transition-all duration-300 ${
                scrolled
                  ? "bg-white/80 backdrop-blur-2xl shadow-[0_1px_2px_rgba(0,0,0,0.04)] border-b border-[#DFDFE0]/40"
                  : "bg-transparent backdrop-blur-0 shadow-none border-b border-transparent"
              }`
        }`}
        style={{
          height: HEADER_HEIGHT,
          paddingTop: isMobile ? "env(safe-area-inset-top, 0px)" : undefined,
        }}
      >
        <nav
          className={`relative mx-auto flex h-full max-w-[1280px] items-center px-4 md:px-6 lg:px-10 ${
            isMobile && menuOpen ? "justify-end" : "justify-between"
          }`}
        >
          {/* Logo */}
          <div
            onClick={goHome}
            className={`flex items-center gap-2 cursor-pointer select-none group transition-opacity duration-200 ${
              isMobile && menuOpen
                ? "pointer-events-none opacity-0"
                : "opacity-100"
            }`}
            style={{ minHeight: 44 }}
            aria-hidden={isMobile && menuOpen}
          >
            <LogoIcon className="h-7 w-7 flex-shrink-0 transition-transform duration-300 group-hover:scale-105" />
            <span className="text-[14px] font-bold tracking-[0.12em] text-[#0D2B7E] select-none">
              ETLYN
            </span>
          </div>

          {/* Desktop nav links */}
          {!isMobile && (
            <ul className="absolute left-1/2 flex -translate-x-1/2 items-center gap-1">
              {NAV_ITEMS.map((item) => (
                <li key={item.label}>
                  <Link
                    to={item.route}
                    data-analytics-target={`nav_${item.label.toLowerCase()}`}
                    className={`px-4 py-2 text-[13px] font-medium rounded-full transition-all duration-200 ${
                      isActive(item.route)
                        ? "text-[#0D2B7E] bg-[#EEF1FB]/80"
                        : "text-[#666D7C] hover:text-[#333D51] hover:bg-[#F5F6F8]"
                    }`}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          )}

          {/* Desktop actions */}
          {!isMobile && (
            <div className="flex items-center gap-2.5">
              {authenticated ? (
                (user?.role === "admin" || user?.role === "editor") && (
                  <button
                    onClick={handleAdminAccess}
                    className="inline-flex items-center rounded-full border border-[#0D2B7E] px-4 py-2 text-[13px] font-semibold text-[#0D2B7E] transition-all duration-200 hover:bg-[#EEF1FB]"
                  >
                    Admin Panel
                  </button>
                )
              ) : (
                <button
                  onClick={handleLogin}
                  className="inline-flex items-center rounded-full border border-[#DFDFE0] px-4 py-2 text-[13px] font-medium text-[#666D7C] transition-all duration-200 hover:bg-[#F5F6F8] hover:text-[#333D51]"
                >
                  Log In
                </button>
              )}

              <button
                data-analytics-target="contact"
                onClick={handleContactClick}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#0D2B7E] px-5 py-2 text-[13px] font-semibold text-white shadow-sm transition-all duration-200 hover:bg-[#0A2266] hover:shadow-md hover:-translate-y-px active:translate-y-0"
              >
                Get in touch
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                  />
                </svg>
              </button>
            </div>
          )}

          {/* Mobile hamburger button */}
          {isMobile && (
            <button
              onClick={toggleMenu}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              className={`flex items-center justify-center rounded-full text-[#333D51] transition-colors hover:text-[#0D2B7E] ${
                menuOpen ? "bg-white/80 backdrop-blur-xl" : ""
              }`}
              style={{ minHeight: 44, minWidth: 44 }}
            >
              {menuOpen ? <CloseIcon /> : <HamburgerIcon open={menuOpen} />}
            </button>
          )}
        </nav>
      </header>

      {/* ─── Mobile fullscreen overlay menu ─────────────── */}
      {isMobile && (
        <>
          {/* Backdrop */}
          <div
            className={`fixed inset-0 z-40 bg-black/45 backdrop-blur-md transition-opacity duration-300 ${
              menuOpen
                ? "opacity-100 pointer-events-auto"
                : "opacity-0 pointer-events-none"
            }`}
            onClick={closeMenu}
            aria-hidden="true"
          />

          {/* Slide-in panel */}
          <div
            className={`fixed top-0 right-0 z-[45] flex h-full w-[min(70vw,300px)] flex-col bg-white shadow-2xl rounded-l-2xl transition-transform duration-300 ease-out ${
              menuOpen ? "translate-x-0" : "translate-x-full"
            }`}
            style={{ paddingTop: HEADER_HEIGHT }}
          >
            {/* Nav links */}
            <nav className="flex-1 overflow-y-auto px-8 pt-6">
              <ul className="space-y-5">
                {NAV_ITEMS.map((item) => (
                  <li key={item.label}>
                    <button
                      data-analytics-target={`nav_${item.label.toLowerCase()}`}
                      onClick={() => goToRoute(item.route)}
                      className={`text-xl font-semibold transition-all rounded-lg px-3 py-1.5 ${
                        isActive(item.route)
                          ? "text-[#0D2B7E] bg-[#EEF1FB]"
                          : "text-[#333D51] hover:text-[#0D2B7E] hover:bg-[#F5F6F8]"
                      }`}
                      style={{ minHeight: 44 }}
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Bottom actions */}
            <div
              className="border-t border-[#DFDFE0]/40 px-8 pt-5 space-y-3"
              style={{
                paddingBottom:
                  "max(1.5rem, env(safe-area-inset-bottom, 1.5rem))",
              }}
            >
              <button
                data-analytics-target="contact"
                onClick={handleContactClick}
                className="w-full rounded-full bg-[#0D2B7E] py-3 text-sm font-semibold text-white transition hover:bg-[#0A2266]"
              >
                Get in touch
              </button>

              {authenticated ? (
                (user?.role === "admin" || user?.role === "editor") && (
                  <button
                    onClick={handleAdminAccess}
                    className="w-full rounded-full border border-[#0D2B7E] py-2.5 text-sm font-semibold text-[#0D2B7E] transition hover:bg-[#EEF1FB]"
                  >
                    Admin Panel
                  </button>
                )
              ) : (
                <button
                  onClick={handleLogin}
                  className="w-full rounded-full border border-[#DFDFE0] py-2.5 text-sm font-medium text-[#666D7C] transition hover:bg-[#F5F6F8]"
                >
                  Log In
                </button>
              )}

              {/* Social links */}
              <div className="flex items-center justify-center gap-3 pt-2 border-t border-[#DFDFE0]/40">
                <a
                  href="https://www.linkedin.com/company/etlyn"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full text-[#808692] transition-colors hover:bg-[#EEF1FB] hover:text-[#0D2B7E]"
                  aria-label="Etlyn LinkedIn"
                >
                  <LinkedinIcon />
                </a>
                <a
                  href="https://x.com/Etlyn_LLC"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full text-[#808692] transition-colors hover:bg-[#EEF1FB] hover:text-[#0D2B7E]"
                  aria-label="Etlyn X"
                >
                  <XIcon />
                </a>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ─── Header spacer (prevents content from hiding under fixed header) */}
      <div aria-hidden="true" style={{ height: HEADER_HEIGHT }} />
    </>
  );
};
